<template>
  <form class="interactive-social-wall__reply-form" @submit.prevent="submitReply">
    <label :for="replyInputId">
      <strong>{{ replyTargetName }}</strong>
      {{ $t('general.reply_to_user') }}
    </label>
    <textarea :id="replyInputId"
              ref="replyInput"
              v-model="replyDraft"
              :disabled="busy"
              maxlength="1000"
              :placeholder="$t('general.write_your_reply')"
              rows="2"
              @input="clearReplyError"
              @keydown.ctrl.enter.prevent="submitReply"
              @keydown.meta.enter.prevent="submitReply"/>

    <p v-if="error" class="interactive-social-wall__reply-form-error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-circle"/>
      {{ error }}
    </p>

    <div class="interactive-social-wall__reply-form-footer">
      <div>
        <button :disabled="busy"
                class="interactive-social-wall__reply-cancel"
                type="button"
                @click="$emit('close-reply')">
          {{ $t('general.cancel') }}
        </button>
        <button :aria-busy="submitting"
                :disabled="!replyDraft.trim() || busy"
                class="interactive-social-wall__reply-submit"
                type="submit">
          <span v-if="submitting"
                aria-hidden="true"
                class="interactive-social-wall__inline-spinner interactive-social-wall__inline-spinner--light"/>
          {{ submitting ? $t('general.sending') : $t('general.answer') }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { commentId } from "../utils/commentTree";
import { userName } from "../utils/presentation";

export default {
  name: "SocialWallReplyForm",
  props: {
    comment: {
      type: Object,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
  },
  data() {
    const replyTargetName = userName(this.comment?.user);
    return {
      replyDraft:
          replyTargetName && replyTargetName !== "Kullanıcı"
              ? `@${replyTargetName} `
              : "",
    };
  },
  computed: {
    replyTargetName() {
      return userName(this.comment?.user);
    },
    replyInputId() {
      return `interactive-social-wall-reply-${commentId(this.comment)}`;
    },
  },
  mounted() {
    this.$nextTick(this.focusReplyInput);
  },
  methods: {
    clearReplyError() {
      if (this.error) this.$emit("clear-reply-error");
    },
    submitReply() {
      const text = this.replyDraft.trim();
      if (!text || this.busy) return;
      this.$emit("submit-reply", { comment: this.comment, text });
    },
    focusReplyInput() {
      const replyInput = this.$refs.replyInput;
      if (!replyInput) return;
      replyInput.focus();
      replyInput.setSelectionRange(
          this.replyDraft.length,
          this.replyDraft.length
      );
      replyInput.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__reply-form,
.interactive-social-wall__reply-form * {
  box-sizing: border-box;
}

.interactive-social-wall__reply-form button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__reply-form {
  width: 100%;
  margin-top: 5px;
  padding: 9px;
  background: #f8fafc;
  border: 1px solid #e1e7ee;
  border-radius: 7px;
}

.interactive-social-wall__reply-form > label {
  display: block;
  margin-bottom: 6px;
  color: #697487;
  font-size: 8.5px;
  line-height: 1.4;
}

.interactive-social-wall__reply-form > label strong {
  color: #354052;
  font-weight: 700;
}

.interactive-social-wall__reply-form textarea {
  display: block;
  width: 100%;
  min-height: 58px;
  padding: 8px 9px;
  color: #202734;
  font-family: var(--font-primary, "Poppins", sans-serif);
  font-size: 10px;
  line-height: 1.5;
  resize: vertical;
  background: #ffffff;
  border: 1px solid #d7dee7;
  border-radius: 6px;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.interactive-social-wall__reply-form textarea::placeholder {
  color: #929baa;
}

.interactive-social-wall__reply-form textarea:focus {
  border-color: #72c861;
  box-shadow: 0 0 0 3px rgba(80, 184, 62, 0.1);
}

.interactive-social-wall__reply-form textarea:disabled {
  cursor: wait;
  opacity: 0.7;
  background: #f5f7f9;
}

.interactive-social-wall__reply-form-error {
  --iconColor: #d84f4f;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 6px 0 0;
  color: #c74444;
  font-size: 8.5px;
  line-height: 1.4;
}

.interactive-social-wall__reply-form-error i {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 11px;
}

.interactive-social-wall__reply-form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.interactive-social-wall__reply-form-footer > div {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.interactive-social-wall__reply-cancel,
.interactive-social-wall__reply-submit {
  min-height: 29px;
  padding: 0 10px !important;
  font-size: 8.5px !important;
  font-weight: 600 !important;
  border-radius: 5px;
}

.interactive-social-wall__reply-cancel {
  color: #68758b !important;
  background: #ffffff !important;
  border: 1px solid #dde3ea !important;
}

.interactive-social-wall__reply-submit {
  color: #ffffff !important;
  background: #50b83e !important;
  border: 1px solid #50b83e !important;
}

.interactive-social-wall__reply-cancel:disabled,
.interactive-social-wall__reply-submit:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.interactive-social-wall__inline-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

.interactive-social-wall__inline-spinner--light {
  margin-right: 6px;
  border-color: rgba(255, 255, 255, 0.42);
  border-top-color: #ffffff;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .interactive-social-wall__reply-form textarea {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__reply-form {
    padding: 8px;
  }

  .interactive-social-wall__reply-cancel,
  .interactive-social-wall__reply-submit {
    min-height: 34px;
    padding-right: 12px !important;
    padding-left: 12px !important;
  }
}
</style>
